import { useEffect, useMemo, useState } from 'react';
import { GetUsageRequest } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { useQueryClient } from 'react-query';
import { useLimitAudience, keys, useGetUsage, useGetAudience } from '@frontend/api-bulk-messaging';
import { MessagingEtlAmpQueries } from '@frontend/api-messaging-etl-amp';
import { useTranslation } from '@frontend/i18n';
import {
  MessageAllotmentsFilters,
  type LocationUsageAndAllotmentWithLimit,
} from '@frontend/message-allotments-filters';
import { useAppScopeStore } from '@frontend/scope';
import type { Data } from '@frontend/segmented-bar';
import { theme } from '@frontend/theme';
import { useAudienceTray } from '../providers/audience-tray-provider';

export const AllotmentsView = () => {
  const { t } = useTranslation('bulk-messaging');
  const { accessibleLocationIds, selectedOrgId: orgId } = useAppScopeStore();
  const { campaignId, campaignType, locationIds, setMode } = useAudienceTray();
  const [dataWithLimit, setDataWithLimit] = useState<LocationUsageAndAllotmentWithLimit[]>([]);
  const { data: organizationData = [] } = MessagingEtlAmpQueries.useGetLocation({ orgId });
  const distinctParentLocationIds = [...new Set(organizationData?.map((location) => location.parentId))];
  const locationIdsWithoutParent = organizationData
    ?.filter((location) => !distinctParentLocationIds.includes(location.locationId))
    .map((location) => location.locationId);

  // Importing the `useBulkFeatureAccess` hook from the bulk-messaging feature isn't going to work here, but this allotments view wouldn't be available if they can't access the feature so this might be fine
  const usageRequest: GetUsageRequest = {
    locationIds: locationIds ?? accessibleLocationIds,
    orgId,
    type: campaignType,
    year: new Date().getFullYear(),
  };
  const usageMonth = new Date().getMonth() + 1;
  const queryKey = keys.getUsage(usageRequest);

  const { data: usageData = {}, isFetched: usageDataIsFetched } = useGetUsage(usageRequest, usageMonth);
  const filteredData = useMemo(
    () =>
      Object.entries(usageData).filter(([locationId, _locationUsage]) => locationIdsWithoutParent.includes(locationId)),
    [usageData, locationIdsWithoutParent.sort().join(',')]
  );

  const getUsageAndRemainingLimit = (item: LocationUsageAndAllotmentWithLimit) => {
    const totalUsed = (item.sent ?? 0) + (item.scheduled ?? 0) + (item.error ?? 0);
    const totalAvailable = (item.allotment ?? 0) - totalUsed;
    return { totalAvailable, totalUsed };
  };

  const { data: audience, isFetched: audienceIsFetched } = useGetAudience({
    orgId,
    campaignId: campaignId ?? '',
  });

  const getContactCount = (locationId: string) =>
    audience?.counts?.find((location) => location.locationId === locationId)?.count ?? 0;

  useEffect(() => {
    if (!audience || !filteredData.length) return;
    if (audienceIsFetched && usageDataIsFetched) {
      setDataWithLimit(
        filteredData.map(([locationId, locationUsage]) => {
          const monthUsage = locationUsage.usage[0];
          const limit = audience?.counts?.find((location) => location.locationId === locationId)?.count ?? 0;
          return {
            locationId,
            month: monthUsage.month,
            allotment: monthUsage.allotment,
            sent: monthUsage.sent,
            scheduled: monthUsage.scheduled,
            error: monthUsage.error,
            limit,
          };
        })
      );
    }
  }, [filteredData, audience?.counts]);

  const queryClient = useQueryClient();
  const { mutate: limitAudience } = useLimitAudience({
    onSuccess: () => {
      queryClient.invalidateQueries(queryKey);
    },
  });

  const chartData = dataWithLimit.reduce<Record<string, Data[]>>((acc, item) => {
    const { totalAvailable, totalUsed } = getUsageAndRemainingLimit(item);
    if (!item.locationId) return acc;
    return {
      ...acc,
      [item.locationId]: [
        {
          data: totalUsed,
          label: t('To be used in this send'),
          color: theme.colors.secondary.eggplant40,
          hoverLabel: t('{{messageCount}} Used by This Send', { messageCount: totalUsed }),
        },
        {
          data: totalAvailable,
          label: t('Remaining in allotment'),
          color: theme.colors.neutral20,
          hoverLabel: t('{{messageCount}} Available', { messageCount: totalAvailable }),
          isRemaining: true,
        },
      ],
    };
  }, {});

  return (
    <MessageAllotmentsFilters
      audienceCounts={audience?.counts}
      chartData={chartData}
      data={dataWithLimit}
      isLoading={!audienceIsFetched || !usageDataIsFetched}
      onBack={() => setMode('segments')}
      onSave={() => {
        if (campaignId) {
          const limits = dataWithLimit.map((item) => {
            const contactCount = getContactCount(item.locationId!);
            const { totalAvailable } = getUsageAndRemainingLimit(item);
            const count = item.limit ?? 0;
            const exceedsContactCount = count > contactCount && contactCount < totalAvailable;
            const exceedsTotalAvailable = count > totalAvailable;

            return {
              locationId: item.locationId ?? '',
              count: exceedsContactCount ? contactCount : exceedsTotalAvailable ? totalAvailable : count,
            };
          });
          limitAudience({ orgId, campaignId, limits });
        }

        setMode('segments');
      }}
      setData={setDataWithLimit}
    />
  );
};
